/**
 * @file stock-row-actions.ts
 * @description Реестр действий «⋯» строки остатка (phase19.24 этап 1, шаг 4).
 *
 * Замечание пользователя: «я хочу разместить, как быстро сделать это из
 * журнала?» — у строки остатка действий не было вовсе, путь к ячейке шёл через
 * карточку ячейки или ручное перемещение.
 *
 * Набор пунктов зависит от состояния строки, и это правило живёт данными, а не
 * ветками в разметке: партия без адреса — «Разместить в ячейку», партия с
 * адресом — «Переставить», беспартийный остаток — ни то, ни другое (адрес
 * хранения в системе принадлежит партии, сервер такую строку отклонит
 * `transfer_lot_required`). «Движения» и «Открыть товар» есть всегда.
 *
 * Адреса переходов собираются здесь же и покрыты тестом: ссылка «Движения по
 * партии» обязана открыть журнал с тем же складом, иначе кладовщик увидит
 * чужие движения и решит, что партия потерялась.
 */

import { History, MapPin, MoveRight, Package } from "lucide-react";

import { PLACEMENT_TEXT } from "./stock-placement-texts";
import type { RowAction } from "../../ui/RowActionsMenu";
import type { StockLevelDto } from "../../../lib/api-wms";

/** Что строка умеет: размещение живёт только у партии. */
export interface StockRowHandlers {
  /** Открыть диалог размещения/перестановки партии. */
  onMoveBin: (level: StockLevelDto) => void;
  /** Уйти по адресу внутри админки (журнал движений, карточка товара). */
  onNavigate: (href: string) => void;
}

/** Пути, от которых считаются ссылки: `/shop/:slug` и `/shop/:slug/wms`. */
export interface StockRowPaths {
  shopPath: string;
  wmsPath: string;
}

/**
 * Журнал движений по этой строке: по номеру партии, а у беспартийного
 * остатка — по артикулу (номера у него нет, а поиск журнала артикул понимает).
 * Склад всегда подставляем: движения соседнего склада к этой строке отношения
 * не имеют.
 *
 * Период — 90 дней, а не 30 по умолчанию: партия живёт дольше месяца, и на
 * окне «за 30 дней» приход, которым её завели, в журнал уже не попадал —
 * выглядело как «партия взялась из ниоткуда» (правка по ревью 2026-09-20).
 */
export function stockMovementsHref(wmsPath: string, level: StockLevelDto): string {
  const params = new URLSearchParams({ view: "movements" });
  // phase19.31 (SC59): номер партии — в общий поиск (`q`), отдельного поля
  // «Партия» больше нет; старые ссылки с `lot=` понимает `useStockFilters`.
  // Номер ставим целиком и без лишнего: журнал, увидев в поиске ровно номер
  // партии, сравнивает точно (`searchMovements`) — «…-1» не находит «…-10».
  const query = level.lot?.batchNumber ?? level.sku?.code;
  if (query) params.set("q", query);
  if (level.warehouseId) params.set("wh", level.warehouseId);
  params.set("period", "90");
  return `${wmsPath}/stock?${params.toString()}`;
}

/** Карточка товара каталога; `null` — сервер не прислал товар у фасовки. */
export function stockProductHref(
  shopPath: string,
  level: StockLevelDto,
): string | null {
  const productId = level.sku?.product?.id;
  return productId ? `${shopPath}/catalog/products/${productId}` : null;
}

/** Пункты «⋯» по состоянию строки. Порядок фиксирован: работа → просмотр. */
export function stockRowActions(
  level: StockLevelDto,
  paths: StockRowPaths,
  handlers: StockRowHandlers,
): RowAction[] {
  const actions: RowAction[] = [];

  if (level.lot) {
    const placed = Boolean(level.lot.bin);
    actions.push({
      key: placed ? "move-bin" : "place",
      label: placed ? PLACEMENT_TEXT.actionMove : PLACEMENT_TEXT.actionPlace,
      icon: placed ? MoveRight : MapPin,
      run: () => handlers.onMoveBin(level),
    });
  }

  actions.push({
    key: "movements",
    label: level.lot
      ? PLACEMENT_TEXT.actionLotMovements
      : PLACEMENT_TEXT.actionSkuMovements,
    icon: History,
    run: () => handlers.onNavigate(stockMovementsHref(paths.wmsPath, level)),
  });

  const productHref = stockProductHref(paths.shopPath, level);
  if (productHref) {
    actions.push({
      key: "product",
      label: PLACEMENT_TEXT.actionProduct,
      icon: Package,
      run: () => handlers.onNavigate(productHref),
    });
  }

  return actions;
}

/** Партию можно разместить: она есть и адреса у неё пока нет. */
export function isPlaceable(level: StockLevelDto): boolean {
  return Boolean(level.lot && !level.lot.bin);
}
